import * as yup from "yup";

import {exists, generateUniqueKey, getReadableKey, toTitleCase} from "./helpers.js";
import {formatFormulaToSave} from "../pages/checksheet-builder/formulaHelpers.js";

// in pixels, for formatting in UI
export const EMPTY_TABLE_HEIGHT = 200;
export const TABLE_ROW_HEIGHT = 36;
export const TABLE_HEADER_HEIGHT = TABLE_ROW_HEIGHT * 2;

export const calculateSectionHeight = item => {
  if (item.type === "table") {
    return item.data.length * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT;
  }
  return 0;
};

export const GROUP = "group";
export const COMPONENT = "component";
export const FIELD = "field";

export const FIELD_TYPES = [
  {
    value: "checkbox",
    label: "Check Box"
  },
  {
    value: "dropdown",
    label: "Drop Down"
  },
  {
    value: "text",
    label: "Text"
  },
  {
    value: "radio",
    label: "Radio"
  },
  {
    value: "multiselect",
    label: "Multi Select"
  },
  {
    value: "number",
    label: "Number"
  },
  {
    value: "textarea",
    label: "Text Box"
  },
  {
    value: "date",
    label: "Date"
  },
  {
    value: "switch",
    label: "Switch"
  },
  {
    value: "confirm",
    label: "Confirm"
  },
  {
    value: "weather",
    label: "Weather"
  },
  {
    value: "upload",
    label: "Upload"
  }
];

export const defaultUnacceptableParameterPrompt =
  "Unacceptable parameter - please provide context.";

// Field Validation
export const DEFAULT_DROPDOWN_PLACEHOLDER = "Select One...";

export const initialFieldValues = {
  type: "number",
  label: "",
  prompt: "",
  required: true,
  // Number
  degree: "Number",
  units: "",
  tag: "",
  hasRange: false,
  min: null,
  max: null,
  rangeMin: null,
  rangeMax: null,
  hasARange: false,
  aRangeComment: true,
  aMin: null,
  aMax: null,
  strictMin: false,
  strictMax: false,
  hasSetPoint: false,
  setLow: null,
  setHigh: null,
  hasLimits: false,
  limits: [{value: null}],
  rangeException: defaultUnacceptableParameterPrompt,
  restrictedRangeException: "",
  hasQualifier: false,
  depends: null,
  formula: null,
  // Dropdown
  placeholder: DEFAULT_DROPDOWN_PLACEHOLDER,
  other: false,
  // Radio
  orient: false,
  // MultiSelect
  all: false,
  // Textarea
  maxLength: 200,
  // Switch
  on: "ON",
  off: "OFF",
  // Dropdown, MultiSelect, Radio
  options: [{option: ""}],
  // Confirm
  phones: null,
  email: null,
  // Time
  military: true,
  // Alert
  hasAlert: false,
  alertCondition: null,
  alertMessage: defaultUnacceptableParameterPrompt
};

/**
 * @param {string} label
 * @param {string} name
 * @param {string} parent
 * @param {object} builder
 * @returns {boolean}
 */
export const labelAlreadyExists = (label, name, parent, builder) => {
  const {allIds, byId} = builder;
  const children = (parent && byId[parent]?.children) || [];
  const compare = parent ? children : allIds;
  return compare
    ?.filter(key => !builder.byId[key]?.stale && !builder.byId[key]?.deleted)
    ?.map(key => key !== name && builder.byId[key]?.label)
    .includes(label.toUpperCase());
};

/**
 * @param {string} parent
 * @param {object} builder
 * @param {{skipLabelUniqueness?: boolean}} [options]
 * @returns {object}
 */
export const fieldValidationSchema = (parent, builder, options = {}) => {
  const {skipLabelUniqueness = false} = options;

  let label = yup
    .string()
    .required("Field name is required.")
    .test({
      test: val => !val.match(/.*[.,'"].*/),
      message: "Field name may not contain periods, commas or quotation marks."
    });

  if (!skipLabelUniqueness) {
    label = label.test({
      name: "label-uniqueness-test",
      test: (value, ctx) => {
        if (!builder) return true;

        const newParent = ctx.parent.linkedComponent || ctx.parent.linkedGroup || parent;

        if (labelAlreadyExists(value, ctx.parent?.name, newParent, builder))
          return ctx.createError({
            message: newParent
              ? `Field name already exists in ${builder.byId[newParent].label}`
              : "Field name already exists on top level",
            path: ctx.path
          });

        return true;
      }
    });
  }

  return yup.object().shape(
    {
      type: yup.string().required(),
      label,
      tag: yup.string().nullable(),
      prompt: yup.string(),
      required: yup.bool(),
      // Number
      degree: yup.lazy((_value, ctx) =>
        ctx.parent.type === "number" || ctx.parent.type === "range"
          ? yup.string().required("Degree is required")
          : yup.mixed().notRequired()
      ),
      units: yup.lazy((_value, ctx) =>
        ctx.parent.type === "number" ||
        ctx.parent.type === "generated" ||
        ctx.parent.type === "range"
          ? yup.string().nullable().required("Units is required.")
          : yup.mixed().notRequired()
      ),
      hasPrecision: yup.boolean(),
      precision: yup
        .string()
        .nullable()
        .when("hasPrecision", {
          is: val => !!val,
          then: () =>
            yup.lazy(value => {
              if (value === "") return yup.string().required("Please provide decimal precision.");
              return yup
                .number()
                .min(1, "Precision must be a value between 1 and 8.")
                .max(8, "Precision must be a value between 1 and 8.");
            })
        }),
      rangeMin: yup
        .mixed()
        .nullable()
        .when("type", {
          is: val => val === "range",
          then: () =>
            yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .nullable()
              .typeError("Min must be a number.")
              .required("Min is required.")
        }),
      rangeMax: yup
        .mixed()
        .nullable()
        .when("type", {
          is: val => val === "range",
          then: () =>
            yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .nullable()
              .typeError("Max must be a number.")
              .required("Max is required.")
        }),
      hasRange: yup.boolean(),
      min: yup
        .mixed()
        .nullable()
        .when("hasRange", {
          is: val => !!val,
          then: () =>
            yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .nullable()
              .typeError("Min must be a number.")
              .test({
                message: "Either min or max must be provided.",
                test: (min, ctx) => exists(min) || exists(ctx.parent.max)
              })
        }),
      max: yup
        .mixed()
        .nullable()
        .when("hasRange", {
          is: val => !!val,
          then: () =>
            yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .nullable()
              .typeError("Max must be a number.")
              .test({
                message: "Invalid range, please make sure that max value is greater than the min.",
                test: (max, ctx2) =>
                  exists(max) && exists(ctx2.parent.min) ? max > ctx2.parent.min : true
              })
        }),
      restrictedRangeException: yup.string().nullable(),
      hasARange: yup.boolean(),
      aRangeComment: yup.boolean(),
      aMin: yup
        .mixed()
        .nullable()
        .when("hasARange", {
          is: val => !!val,
          then: () =>
            yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .nullable()
              .typeError("Min must be a number.")
              .test({
                message: "Either min or max must be provided.",
                test: (aMin, ctx) => exists(aMin) || exists(ctx.parent.aMax)
              })
        }),
      aMax: yup
        .mixed()
        .nullable()
        .when("hasARange", {
          is: val => !!val,
          then: () =>
            yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .nullable()
              .typeError("Max must be a number.")
              .test({
                message: "Invalid range, please make sure that max value is greater than the min.",
                test: (aMax, ctx2) =>
                  exists(aMax) && exists(ctx2.parent.aMin) ? aMax > ctx2.parent.aMin : true
              })
        }),
      hasSetPoint: yup.boolean(),
      setLow: yup
        .string()
        .nullable()
        .when("hasSetPoint", {
          is: val => !!val,
          then: () => yup.number().typeError("Set point min must be a number.").required()
        }),
      setHigh: yup
        .string()
        .nullable()
        .when(["hasSetPoint", "setLow"], {
          is: (hasRange, setLow) => hasRange && !Number.isNaN(setLow),
          then: () =>
            yup
              .number()
              .typeError("Set point max must be a number.")
              .test({
                message:
                  "Invalid range, please make sure that max value is greater than the set point min.",
                test: (setHigh, ctx2) => setHigh > ctx2.parent.setLow
              })
        }),
      hasLimits: yup.boolean(),
      limits: yup
        .mixed()
        .nullable()
        .when("hasLimits", {
          is: val => !!val,
          then: () =>
            yup.array().of(
              yup.object().shape({
                label: yup.string().required("Please enter a label."),
                value: yup
                  .number()
                  .transform((v, o) => (o === "" ? null : v))
                  .nullable()
                  .typeError("Please enter a valid number.")
                  .required("Please enter a value.")
              })
            )
        }),
      hasQualifier: yup.boolean(),
      // Dropdown
      placeholder: yup.lazy((_value, ctx) =>
        ctx.parent.type === "dropdown" ? yup.string().nullable() : yup.mixed().notRequired()
      ),
      other: yup.lazy((_value, ctx) =>
        ctx.parent.type === "dropdown" ? yup.bool() : yup.mixed().notRequired()
      ),
      // MultiSelect
      all: yup.lazy((_value, ctx) =>
        ctx.parent.type === "multiselect" ? yup.bool() : yup.mixed().notRequired()
      ),
      // Textarea
      maxLength: yup.lazy((_value, ctx) =>
        ctx.parent.type === "textarea"
          ? yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .nullable()
              .typeError("Max Length must be a number.")
              .required("Max Length is required.")
          : yup.mixed().notRequired()
      ),
      // Switch
      on: yup.lazy((_value, ctx) =>
        ctx.parent.type === "switch" ? yup.string() : yup.mixed().notRequired()
      ),
      off: yup.lazy((_value, ctx) =>
        ctx.parent.type === "switch" ? yup.string() : yup.mixed().notRequired()
      ),
      // Options
      options: yup.lazy((_value, ctx) =>
        ctx.parent.type === "dropdown" ||
        ctx.parent.type === "multiselect" ||
        ctx.parent.type === "radio"
          ? yup
              .array()
              .nullable()
              .of(
                yup.object().shape({
                  option: yup
                    .string()
                    .nullable()
                    .test({
                      message: "Option cannot be empty.",
                      test: s => !!s
                    })
                })
              )
              .test({
                message: "Please provide at least two options.",
                test: v => Array.isArray(v) && v.length > 1
              })
              .test({
                message: "Options cannot match",
                test: val => {
                  const o = val.map(({option}) => option);
                  return new Set(o).size === o.length;
                }
              })
          : yup.mixed().notRequired()
      ),
      // Confirm
      phone: yup
        .object()
        .shape({
          number: yup
            .string()
            .nullable()
            .matches(/^\(\d{3}\)\s\d{3}-\d{4}/i, {
              message: "Please provide valid phone #.",
              excludeEmptyString: true
            }),
          extension: yup.string().nullable()
        })
        .nullable(),
      email: yup.string().email("Invalid email address").nullable(),
      hasAlert: yup.bool(),
      alertCondition: yup
        .mixed()
        .nullable()
        .test({
          test: (v, ctx) => {
            if (!Array.isArray(v)) return true;
            return v.length < ctx.parent.options.length;
          },
          message: "Cannot include all options in acceptable parameter check"
        }),
      alertMessage: yup
        .string()
        .nullable()
        .when("hasAlert", {
          is: true,
          then: () => yup.string().required("Message is required.")
        })
    },
    [
      ["min", "max"],
      ["aMin", "aMax"]
    ]
  );
};

const clonePlain = value => {
  if (Array.isArray(value)) return value.map(clonePlain);
  if (!value || typeof value !== "object") return value;
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clonePlain(item)]));
};

/**
 * Deep copy a builder so nested element data (conditions, formulas, options, help) is never
 * shared with the original. Non-plain values such as dates or files are kept by reference.
 * @param {object} builder
 * @returns {object} copy
 */
export const deepCopyBuilder = builder => {
  if (!builder) return builder;
  const copy = clonePlain(builder);
  if (!copy.byId || !copy.allIds) return copy;
  copy.byId = Object.fromEntries(
    Object.entries(copy.byId).map(([key, val]) => [key, {...val, children: val?.children || []}])
  );
  return copy;
};

// Add Elements

/**
 * @param {object} builder
 * @param {object} field
 * @param {object} nameMap (store map to new field name for later use)
 * @param {string} originalName (parent field id of generated formula)
 * @returns {object} copy
 */
export const addField = (builder, values, nameMap, originalName) => {
  const byId = {...builder.byId};
  const allIds = [...builder.allIds];
  const {name, label, parentName, help} = values;
  const field = {...values};

  // Set Field Key
  const sanitized = label.replace(/\s+/g, " ").trim();
  field.name = name || generateUniqueKey(sanitized);

  // Set new key based on existing field (for generated fields)
  if (originalName && nameMap) nameMap[originalName] = field.name;

  field.label = sanitized.toUpperCase();
  field.element = "field";
  field.parentName = parentName || "";
  field.children = [];

  if (field.depends && field.depends.length > 0 && !originalName) {
    // dependencies handled separately when original name is passed (cloning and template apply)
    field.depends.map(dependency => {
      if (dependency in byId && byId[dependency].generates)
        byId[dependency].generates.push(field.name);
      else if (dependency in byId) byId[dependency].generates = [field.name];
    });
    if (
      field.type === "generated" &&
      field.depends.length > 0 &&
      byId[field.depends[0]] &&
      byId[field.depends[0]].precision
    ) {
      field.precision = byId[field.depends[0]].precision;
      field.hasPrecision = true;
    }
  }

  if (help && parentName && parentName !== "") {
    if (
      !byId[parentName].help ||
      (byId[parentName].help.length === 1 && Object.keys(byId[parentName].help[0]).length === 0)
    )
      byId[parentName].help = [];
    byId[parentName].help.push({key: field.label, value: help, id: field.name});
  }

  delete field.help;

  byId[field.name] = field;

  if (parentName) byId[parentName].children.push(field.name);
  else allIds.push(field.name);

  return {...builder, byId, allIds};
};

/**
 * @param {object} builder
 * @param {object} field
 * @returns {object} copy
 */
export const addComponent = (builder, values, nameMap) => {
  let byId = {...builder.byId};
  let allIds = [...builder.allIds];

  const {name, label, parentName, children, base, grid, help, toggle} = values;

  const component = {
    element: "component",
    name: name || generateUniqueKey(label),
    label: label.toUpperCase(),
    parentName: parentName || "",
    children: [],
    base: base || false,
    grid: grid || false
  };

  // Optional states
  if (help && help.length > 0 && help[0].key) component.help = help;
  else delete component.help;

  if (toggle) component.toggle = toggle;

  if (parentName) byId[parentName].children.push(component.name);
  else allIds.push(component.name);

  byId[component.name] = component;

  // Set Component Fields
  if (children?.length > 0)
    children.map(childName => {
      const childCopy = {
        ...byId[childName],
        label: `${byId[childName].label}`,
        parentName: component.name,
        generates: []
      };

      delete childCopy.name;

      if (childCopy.condition) childCopy.condition = {...byId[childName].condition};
      if (childCopy.formula) childCopy.formula = byId[childName].formula.map(part => ({...part}));
      ({byId, allIds} = addField({...builder, byId, allIds}, {...childCopy}, nameMap, childName));
    });

  return {...builder, byId, allIds};
};

/**
 * @param {object} builder
 * @param {object} field
 * @returns {object} copy
 */
export const addGroup = (builder, element, nameMap) => {
  let byId = {...builder.byId};
  let allIds = [...builder.allIds];

  const {
    name,
    label,
    children,
    hasGlobalPrompt,
    globalPrompt,
    hasInternalIds,
    internalIds,
    hasHelp,
    help,
    hasAddress,
    address,
    cloneIdx,
    toggle
  } = element;

  const group = {
    element: "group",
    name: name || generateUniqueKey(label),
    label: label.toUpperCase(),
    parentName: "",
    children: []
  };

  // Optional states
  if (hasGlobalPrompt) group.globalPrompt = globalPrompt;
  else delete group.globalPrompt;

  if (hasInternalIds) group.internalIds = internalIds;
  else delete group.internalIds;

  if (hasHelp) group.help = help;
  else delete group.help;

  if (hasAddress) group.address = address;
  else delete group.address;

  // State that changes for preview
  if (toggle) group.toggle = toggle;

  // Update name during clone
  if (cloneIdx || cloneIdx === 0) allIds.splice(cloneIdx, 0, group.name);
  else allIds.push(group.name);

  byId[group.name] = group;

  if (children?.length > 0)
    children.map(childName => {
      const childCopy = {
        ...byId[childName],
        label: `${byId[childName].label}`,
        parentName: group.name,
        children: [...byId[childName].children]
      };

      delete childCopy.name;

      if (childCopy.element === COMPONENT)
        ({byId, allIds} = addComponent({...builder, byId, allIds}, {...childCopy}, nameMap));
      else {
        if (childCopy.condition) childCopy.condition = {...byId[childName].condition};
        if (childCopy.formula) childCopy.formula = [...byId[childName].formula];
        ({byId, allIds} = addField(
          {...builder, byId, allIds},
          {...childCopy, generates: []},
          nameMap,
          childName
        ));
      }
    });

  return {...builder, byId, allIds};
};

// Remove Elements

/**
 * Remove field in place from byId
 * @param {object} byId
 * @param {name} string
 */
const removeElementFromBuilderObject = (byId, name, keep = false) => {
  if (name in byId && "children" in byId[name]) {
    byId[name].children.forEach(childName => {
      removeElementFromBuilderObject(byId, childName, keep);
    });
  }
  if (name in byId) {
    if (!keep) delete byId[name];
    else {
      byId[name].deleted = true;
      byId[name] = {...byId[name]};
    }
  }
};

/**
 * Remove in place (allIds/children)
 * @param {object} byId
 * @param {array} allIds
 * @param {name} string
 */
const removeNameFromChildrenList = (byId, allIds, name) => {
  const {parentName} = byId[name];

  const ids = parentName ? byId[parentName].children : allIds;
  const removeIndex = ids.indexOf(name);

  if (parentName) byId[parentName].children.splice(removeIndex, 1);

  if (ids[removeIndex] === name) allIds.splice(removeIndex, 1);
};

/**
 * @param {object} builder
 * @param {object} targetElement
 * @returns {object} copy
 */
export const removeElement = (builder, targetElement, keep = false) => {
  const byId = {...builder.byId};
  const allIds = [...builder.allIds];
  const {name, element} = targetElement;

  if (byId[name]) {
    const oldParentName = byId[name].parentName;
    if (element === FIELD && oldParentName && oldParentName !== "") {
      if (byId[oldParentName].help) {
        byId[oldParentName].help = byId[oldParentName].help.filter(h => h.id !== name);
        if (byId[oldParentName].help.length === 0) {
          byId[oldParentName].hasHelp = false;
          byId[oldParentName].help = [{}];
        }
      }
    }

    // field or component, delete id from parent's children/top-level allIds
    if (!keep) removeNameFromChildrenList(byId, allIds, name);

    if (byId[name]?.depends?.length) {
      byId[name].depends.forEach(depName => {
        if (byId[depName]?.generates) {
          byId[depName].generates = byId[depName].generates.filter(fieldName => fieldName !== name);
        }
      });
    }

    // delete all children elements first
    removeElementFromBuilderObject(byId, name, keep);

    // delete current element
    if (!keep) delete byId[name];
    else byId[name].deleted = true;
  }

  return {...builder, byId, allIds};
};

// Edit Elements

export const sortHelp = (help, children, byId) => {
  if (!help) return help;
  const fieldHelp = help.filter(item => item.id in byId);
  const newHelp = help.filter(item => !(item.id in byId));
  return [...newHelp, ...fieldHelp.sort((a, b) => children.indexOf(a.id) - children.indexOf(b.id))];
};

const isActiveBuilderId = (byId, id) => {
  const node = byId[id];
  return !!node && !node.stale && !node.deleted;
};

/**
 * Sibling ids for position controls: top-level `allIds` or a parent's `children`.
 * When `effectiveParentName` is provided (e.g. a pending linked parent), that list is used.
 * Ensures `name` appears in the list so a newly reparented item still has a slot.
 * @param {object} builder
 * @param {string} name
 * @param {string} [effectiveParentName]
 * @returns {{siblingIds: string[], index: number}}
 */
export const getSiblingOrder = (builder, name, effectiveParentName) => {
  const {byId, allIds} = builder;
  const element = byId[name];
  if (!element) return {siblingIds: [], index: -1};

  const parentName =
    effectiveParentName !== undefined && effectiveParentName !== null
      ? effectiveParentName
      : element.parentName || "";

  let siblingIds = parentName
    ? (byId[parentName]?.children || []).filter(id => isActiveBuilderId(byId, id))
    : (allIds || []).filter(id => isActiveBuilderId(byId, id));

  if (!siblingIds.includes(name)) siblingIds = [...siblingIds, name];

  return {siblingIds, index: siblingIds.indexOf(name)};
};

/**
 * Current Position select value: the 0-based slot as a string, or "" when there is only one slot.
 * @param {object} builder
 * @param {string} name
 * @param {string} [effectiveParentName]
 * @returns {string}
 */
export const getElementPosition = (builder, name, effectiveParentName) => {
  const {siblingIds, index} = getSiblingOrder(builder, name, effectiveParentName);
  return siblingIds.length > 1 ? String(index) : "";
};

/**
 * Options for a Position select: `{ name, value, label }` with 0-based index values.
 * Returns an empty array when there is only one slot (control should be hidden).
 * @param {object} builder
 * @param {string} name
 * @param {string} [effectiveParentName]
 * @returns {{name: string, value: string, label: string}[]}
 */
export const getPositionOptions = (builder, name, effectiveParentName) => {
  const {siblingIds} = getSiblingOrder(builder, name, effectiveParentName);
  if (siblingIds.length <= 1) return [];

  return siblingIds.map((id, i) => ({
    name: String(i),
    value: String(i),
    label: `${i + 1} — ${builder.byId[id]?.label || id}`
  }));
};

/**
 * Move an element to `toIndex` within its parent `children` (or top-level `allIds`).
 * Returns a new builder copy; does not mutate the input. Mirrors drag-and-drop splice order.
 * @param {object} builder
 * @param {string} name
 * @param {number} toIndex
 * @returns {object}
 */
export const moveElementInParent = (builder, name, toIndex) => {
  const {byId, allIds} = builder;
  const element = byId[name];
  if (!element) return builder;

  const parentName = element.parentName || "";
  const ids = parentName ? byId[parentName]?.children || [] : allIds || [];
  // Position UI indexes active siblings only; keep deleted/stale ids in place
  // and reorder the active subsequence to match the selected slot.
  const activeSlots = ids.flatMap((id, i) => (isActiveBuilderId(byId, id) ? [i] : []));
  const activeIds = activeSlots.map(i => ids[i]);
  const fromActive = activeIds.indexOf(name);

  if (fromActive === -1 || toIndex === fromActive) return builder;
  if (toIndex < 0 || toIndex >= activeIds.length) return builder;

  const nextActive = [...activeIds];
  const [removed] = nextActive.splice(fromActive, 1);
  nextActive.splice(toIndex, 0, removed);

  const nextIds = [...ids];
  activeSlots.forEach((slot, i) => {
    nextIds[slot] = nextActive[i];
  });

  if (!parentName) return {...builder, allIds: nextIds};

  const parent = byId[parentName];
  if (!parent) return builder;

  const help = sortHelp(parent.help, nextIds, byId);
  return {
    ...builder,
    byId: {
      ...byId,
      [parentName]: {...parent, children: nextIds, ...(help ? {help} : {})}
    }
  };
};

/**
 * Apply a Position select value after an edit/reparent. No-op when position is empty/invalid.
 * @param {object} builder
 * @param {string} name
 * @param {string|number|null|undefined} position
 * @returns {object}
 */
export const applyElementPosition = (builder, name, position) => {
  if (position === null || position === undefined || position === "") return builder;
  const toIndex = Number(position);
  if (Number.isNaN(toIndex)) return builder;
  return moveElementInParent(builder, name, toIndex);
};

/**
 * Updates elements
 * @param {object} builder
 * @param {object} values
 * @returns {object} object with builder and notifications
 */
export const updateElement = (
  builder,
  values,
  regenKey = true,
  nameMap = null,
  forceNewKey = false,
  stale = false,
  notifications = null
) => {
  let {byId, allIds} = builder;

  const {
    name,
    label,
    parentName,
    help,
    globalPrompt,
    internalIds,
    address,
    fields,
    markerContent,
    condition,
    templateId,
    allowDisable
  } = values;

  const oldParentName = byId[name].parentName;
  if (
    values.element === FIELD &&
    oldParentName &&
    oldParentName !== "" &&
    byId[oldParentName].help
  ) {
    byId[oldParentName].help = byId[oldParentName].help.filter(h => h.id !== name);
    if (byId[oldParentName].help.length === 0) delete byId[oldParentName].help;
  }

  let element = byId[name];

  const staleItem = stale ? {...element} : null;

  element = element ? {...element, ...values} : {...values};

  if (!globalPrompt) delete element.globalPrompt;
  if (!internalIds?.length) delete element.internalIds;
  if (!help?.length) delete element.help;
  if (!address || !Object.keys(address).length) delete element.address;
  if (!templateId && element?.templateId) delete element.templateId;
  if (!allowDisable) {
    delete element.enabledLabel;
    delete element.disabledLabel;
  }

  // Field name changed
  if (label && (label !== byId[name].label || forceNewKey)) {
    const newKey = generateUniqueKey(label);
    if (regenKey || forceNewKey) {
      // Type change caused stale field - field duplicated with new key
      if (stale) {
        byId[element.name] = {...staleItem, stale: newKey};
        const matches = Object.keys(byId).filter(key => byId[key].stale === element.name);
        matches.map(match => {
          byId[match].stale = newKey;
        });
        // Track name change
        if (nameMap) nameMap[element.name] = newKey;
      }

      element.name = newKey;
    }
    element.label = label.toUpperCase();
    element.parentName = parentName;

    // Update children's parentName
    const currentChildren = byId[name].children;
    currentChildren.forEach(id => {
      if (byId[id]) byId[id].parentName = element.name;
    });

    // Update parent's children value
    const ids = parentName ? byId[parentName].children : allIds;
    const updateIndex = ids.indexOf(name);

    if (stale) {
      if (parentName) byId[parentName].children.splice(updateIndex, 0, element.name);
      else allIds.splice(updateIndex, 0, element.name);
    } else {
      if (parentName) byId[parentName].children[updateIndex] = element.name;
      else allIds[updateIndex] = element.name;
      delete byId[name];
    }
  }

  // Linked to different group or component
  if (parentName !== null && parentName !== undefined && parentName !== oldParentName) {
    // New Parent Set
    if (parentName !== "" && byId[parentName]) {
      byId[parentName].children.push(element.name);

      if (oldParentName === "") {
        // Remove key from allIds
        const updateAll = allIds.indexOf(name);
        allIds.splice(updateAll, 1);
      } else {
        // Remove key from children
        const childIndex = byId[oldParentName] && byId[oldParentName].children.indexOf(name);
        byId[oldParentName].children.splice(childIndex, 1);
      }
    } else {
      allIds.push(element.name);
      // Remove key from children
      const updateChildren = byId[oldParentName] && byId[oldParentName].children.indexOf(name);
      byId[oldParentName].children.splice(updateChildren, 1);
    }
  }

  element.parentName = parentName;

  if (element.element === FIELD && help && help !== "" && parentName && parentName !== "") {
    if (!byId[parentName].help) byId[parentName].help = [];

    byId[parentName].help.push({key: label, value: help, id: element.name});

    byId[parentName].help = sortHelp(byId[parentName].help, byId[parentName].children, byId);
  }

  if (element.element === FIELD || !help?.length || !element.help?.some(({key}) => !!key))
    delete element.help;

  if (markerContent) element.markerContent = markerContent;

  if (!globalPrompt) delete element.globalPrompt;
  if (!internalIds?.length) delete element.internalIds;
  if (!address || !Object.keys(address).length) delete element.address;

  byId[element.name] = element;

  const updatedById = {...byId};
  const updatedAllIds = [...allIds];

  Object.entries(byId)
    .filter(
      ([
        ,
        {
          formula: dependentFormula,
          trueFormula: dependentTrueFormula,
          condition: dependentCondition
        }
      ]) =>
        dependentFormula?.some(({value}) => value === name) ||
        dependentTrueFormula?.some(({value}) => value === name) ||
        dependentCondition?.list?.some(con => con.depends === name)
    )
    .map(([dependentName, dependentField]) => {
      if (dependentField.formula) {
        const updatedBuilder = {byId, allIds};
        const dependentFormulaAdjusted = dependentField.formula.map(item =>
          item.value === name
            ? {
                ...item,
                value: element.name
              }
            : item
        );

        const formatted = formatFormulaToSave(updatedBuilder, "edit", {
          ...dependentField,
          formula: dependentFormulaAdjusted
        });
        updatedById[dependentName] = formatted;
      }

      if (dependentField.condition?.list) {
        updatedById[dependentName].condition = {
          ...dependentField.condition,
          list: dependentField.condition.list.map(item => ({
            ...item,
            depends: item.depends === name ? element.name : item.depends
          }))
        };
      }
    });

  byId = updatedById;
  allIds = updatedAllIds;

  // Update notifications that reference field
  const nById = notifications?.byId ? {...notifications.byId} : {};

  if (notifications?.byId)
    Object.keys(notifications?.byId).map(key => {
      const {trigger, conditionArray, depends: conDepends} = notifications.byId[key];

      if (trigger === "Unacceptable Parameter") {
        nById[key].conditionArray = conditionArray.map(({depends: condDepends, ...rest}) => ({
          ...rest,
          depends: condDepends === name ? element.name : condDepends
        }));
      }

      if (conDepends === name) nById[key].depends = element.name;
    });

  // fields is only used by apply template
  if (fields) {
    byId[element.name].children = byId[element.name].children.filter(child => byId[child]?.shared);
    fields.forEach(field => {
      ({byId, allIds} = addField(
        {...builder, byId, allIds},
        {...field, parentName: element.name, name: null, toggle: true},
        nameMap,
        field.name
      ));
    });
  }

  if (condition?.list)
    condition.list.map(item => {
      const {depends: condDepends} = item;
      if (
        condDepends in byId &&
        (!byId[condDepends].generates || !byId[condDepends].generates.includes(element.name))
      ) {
        if (!byId[condDepends].generates) byId[condDepends].generates = [];
        byId[condDepends].generates.push(element.name);
      }
    });

  delete byId[element.name].fields;

  const updatedBuilder = {...builder, byId, allIds};
  const updatedNotifications = notifications
    ? {
        byId: nById,
        allIds: notifications?.allIds || []
      }
    : null;

  return {builder: updatedBuilder, notifications: updatedNotifications};
};

/**
 * @param {Array.<string>} ids
 * @param {object} builder
 * @param {object} result
 * @returns {Array.<string>}
 */
export const flattenBuilderHelper = (ids, builder, result = []) => {
  ids?.map(id => {
    const item = builder.byId[id];
    result.push(id);
    if (item?.children?.length > 0) flattenBuilderHelper(item.children, builder, result);
  });

  return result;
};

/**
 * @param {object} builder
 * @returns {Array.<string>}
 */
export const flattenBuilder = builder => flattenBuilderHelper(builder.allIds, builder);

/**
 * Filters toggle and preview for in-memory builder state. Persisted builders from the API
 * omit `toggle` (stripped on save); pass includeWithoutToggle when serializing those.
 * @param {array} ids
 * @param {object} builder
 * @param {object} newBuilder
 * @param {boolean} removeDeleted
 * @param {boolean} includeWithoutToggle - include nodes even when toggle is missing/false
 * @returns {object}
 */
export const newFromBuilder = (
  ids,
  builder,
  newBuilder,
  removeDeleted = false,
  includeWithoutToggle = false
) => {
  const {byId} = builder;
  ids.map(id => {
    const target = byId[id];
    const included =
      target && (includeWithoutToggle || target.toggle) && (!removeDeleted || !target.deleted);
    if (included) {
      if (target.element !== "field") {
        newBuilder.byId[target.name] = {
          element: target.element,
          name: target.name,
          parentName: target.parentName,
          children: []
        };
        const filteredHelp = target.help?.filter(item => item.id);
        if (filteredHelp?.length) newBuilder.byId[target.name].help = filteredHelp;
        if (target?.templateId) newBuilder.byId[target.name].templateId = target?.templateId;
        if (target?.deleted) newBuilder.byId[target.name].deleted = true;
      } else {
        newBuilder.byId[target.name] = {...target, children: []};
        delete newBuilder.byId[target.name].toggle;
        if ("preview" in target) delete newBuilder.byId[target.name].preview;
      }

      if (target.children.length > 0)
        newFromBuilder(target.children, builder, newBuilder, removeDeleted, includeWithoutToggle);

      if (target.parentName && target.parentName !== "" && newBuilder.byId[target.parentName])
        newBuilder.byId[target.parentName].children.push(target.name);
      else newBuilder.allIds.push(target.name);
    }
  });

  return newBuilder;
};

/**
 * @param {array} ids
 * @param {object} builder
 * @returns {boolean}
 */
export const validBuilder = (ids, builder) => {
  if (!builder) return false;

  const {allIds, byId} = builder;

  if (!byId || !allIds || allIds.length === 0) return false;

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];

    if (byId[id].element !== FIELD && byId[id].children.length === 0) return false;
    if (byId[id].children.length > 0) {
      const result = validBuilder(byId[id].children, builder);
      if (!result) return false;
    }
  }

  return true;
};

/**
 * @param {array} ids
 * @param {object} builder
 * @param {array} invalid
 * @returns {boolean}
 */
export const whichElementInvalid = (ids, builder, invalid, hasToggle = true) => {
  if (!builder) return false;

  const {allIds, byId} = builder;

  if (!byId || !allIds || allIds.length === 0) return false;

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const element = byId[id];

    if (element) {
      if (
        (!hasToggle || element.toggle) &&
        element.element !== FIELD &&
        !element.deleted &&
        !element.stale &&
        (!element.children ||
          element.children.length === 0 ||
          (hasToggle &&
            byId[id].children.every(
              child =>
                byId[child]?.element === FIELD &&
                (!byId[child]?.toggle || byId[child]?.deleted || byId[child]?.stale)
            )))
      ) {
        invalid.push(id);
        if (element.parentName && element.parentName !== "") invalid.push(element.parentName);
      } else if (
        element.element !== FIELD &&
        (!hasToggle || element.toggle) &&
        !element.deleted &&
        !element.stale
      ) {
        whichElementInvalid(element.children, builder, invalid, hasToggle);
      }
    }
  }
  return true;
};

/**
 * @param {string} fieldName
 * @returns {object}
 */
export const replacePseudoFields = fieldName => {
  if (!fieldName) return {sanitized: fieldName, suffix: ""};
  let sanitized = fieldName.replace(/_comment$/, "");
  sanitized = sanitized.replace(/_infinite$/, "");
  sanitized = sanitized.replace(/_qualifier$/, "");
  sanitized = sanitized.replace(/_qualifierEnabled$/, "");
  sanitized = sanitized.replace(/_rainfall$/, "");

  let suffix = "";
  if (sanitized !== fieldName) {
    const temp = fieldName.replace(sanitized, "").replace("_", "");
    suffix = getReadableKey(temp).toUpperCase();
  }
  return {sanitized, suffix};
};

/**
 * True if any ancestor group/component (via parentName chain) is soft-deleted.
 * Used so orphan fields under a deleted component are not validated or shown.
 * @param {object} byId
 * @param {string} key
 * @returns {boolean}
 */
export const hasDeletedAncestor = (byId, key) => {
  if (!key || !byId?.[key]) return false;
  let current = byId[key].parentName;
  while (current) {
    if (byId[current]?.deleted) return true;
    current = byId[current]?.parentName;
  }
  return false;
};

/**
 * Mark name and descendants as deleted in an overrides byId map (state builder diffs).
 * @param {object} overridesById
 * @param {object} lookupById - byId used to walk children (e.g. default or merged)
 * @param {string} name
 */
export const cascadeMarkDeletedInOverrides = (overridesById, lookupById, name) => {
  const src = lookupById[name];
  if (!src) return;
  overridesById[name] = {...(overridesById[name] || src), deleted: true};
  (src.children || []).forEach(child =>
    cascadeMarkDeletedInOverrides(overridesById, lookupById, child)
  );
};

/**
 * Diff a state/variant builder against the default checksheet builder for persistence.
 * Groups/components and newly added elements are always stored; fields only when they
 * differ. Clears cascade-delete marks for elements still present and active in `b`.
 *
 * Active overrides always include `deleted: false` so server-side construct_builder
 * merge cannot keep a soft-delete tombstone from the default checksheet builder
 * (default may still retain deleted field defs for history).
 *
 * @param {object} b - state builder `{allIds, byId}`
 * @param {object} defaultBuilder - default checksheet builder `{allIds, byId}`
 * @returns {{allIds: string[], byId: object}}
 */
export const getBuilderOverrides = (b, defaultBuilder) => {
  const overrides = {allIds: [...(b?.allIds || [])], byId: {}};
  const defaultById = defaultBuilder?.byId || {};
  const builderById = b?.byId || {};
  const lookupById = {...defaultById, ...builderById};

  Object.keys(defaultById).forEach(name => {
    if (!(name in builderById) || builderById[name]?.deleted)
      cascadeMarkDeletedInOverrides(overrides.byId, lookupById, name);
  });

  Object.entries(builderById).forEach(([name, attributes]) => {
    if (
      (attributes.deleted && !defaultById[name]?.deleted) ||
      !(name in defaultById) ||
      defaultById[name].deleted ||
      attributes.element !== FIELD
    ) {
      // Explicit false required: construct_builder merges onto default byId which may
      // still have deleted:true for soft-removed groups/fields.
      overrides.byId[name] = attributes.deleted ? {...attributes} : {...attributes, deleted: false};
    } else if (defaultById[name].parentName !== attributes.parentName) {
      overrides.byId[name] = {parentName: attributes.parentName, deleted: false};
    } else if (overrides.byId[name]?.deleted && !attributes.deleted) {
      // Still present in this variant — undo cascade-delete from a removed ancestor.
      // Keep an explicit active marker so default tombstones cannot win on merge.
      overrides.byId[name] = {deleted: false};
    }
  });

  return overrides;
};

/**
 * @param {string} parentName
 * @param {object} byId
 * @returns {string}
 */
export const getAncestors = (parentName, byId) => {
  const {sanitized, suffix} = replacePseudoFields(parentName);

  const parent = sanitized && byId[sanitized];
  const grandParent = parent && parent.parentName && byId[parent.parentName];

  let overrideLabel = parent?.label;
  if (suffix && overrideLabel) overrideLabel = `${overrideLabel} ${suffix}`;

  let ancestors = "";
  if (parent && grandParent) ancestors = `${grandParent.label} ${overrideLabel}`;
  if (parent && !grandParent) ancestors = overrideLabel;

  return ancestors;
};

/**
 * @param {Array.<string>} filter
 * @param {Object} field
 * @returns {Array}
 */
const filtered = (filters, field) => {
  if (field.stale) return false;
  if (!filters) return true;
  return filters.includes(field.type);
};

/**
 * @param {Array} ids
 * @param {Object} byId
 * @param {Array} filters field type filters
 * @param {Array.<object>} result initial array
 * @returns {Array.<object>}
 */
export const getFields = (
  ids,
  byId,
  filters = null,
  result = [],
  showComments = false,
  alsoDisabledComponents = false,
  deleted = false
) => {
  ids
    .filter(id => byId[id] && (!byId[id].deleted || deleted))
    .map(id => {
      const field = byId[id];
      const ancestors = getAncestors(field.parentName, byId);

      if (
        byId[id] &&
        (byId[id].element === "field" ||
          (alsoDisabledComponents && byId[id].element === "component" && byId[id].allowDisable)) &&
        ((!deleted && !byId[id].deleted) || (deleted && byId[id].deleted)) &&
        filtered(filters, byId[id])
      ) {
        let value = ancestors ? `${ancestors} ${field.label}` : field.label;

        if (field.element === "component") value = `${value} Enabled / Disabled`;

        // case for rainfall
        if (field.type === "weather")
          result.push({
            ...field,
            name: `${field.name}_rainfall`,
            label: "Rainfall",
            ancestry: `${toTitleCase(value)} Rainfall`
          });

        // case to include unacceptable parameter comments
        if ((field.hasARange || field.alertStartAfterStop) && showComments)
          result.push({
            ...field,
            name: `${field.name}_comment`,
            label: "Comment",
            ancestry: `${toTitleCase(value)} Comment `
          });

        result.push({
          ...field,
          ancestry: toTitleCase(value)
        });
      }

      if (byId[id] && byId[id].children.length > 0)
        getFields(
          byId[id].children,
          byId,
          filters,
          result,
          showComments,
          alsoDisabledComponents,
          deleted
        );
    });

  return result;
};

/**
 * Whether the facility builder defines fields usable for facility-scoped analytics.
 * @param {object} facility
 * @param {Array<string>} types field type filters (defaults to graphable stat/chart types)
 * @returns {boolean}
 */
export const hasFacilityGraphableFields = (
  facility,
  types = ["number", "generated", "range", "weather"]
) => {
  if (!facility?.builder?.allIds?.length || !facility?.builder?.byId) return false;
  return getFields(facility.builder.allIds, facility.builder.byId, types).length > 0;
};

/**
 * @param {Array} ids
 * @param {Object} byId
 * @param {Array} filters field type filters
 * @param {Array.<object>} result initial array
 * @returns {Array.<object>}
 */
export const getComponents = (ids, byId, result = []) => {
  ids
    .filter(id => byId[id] && !byId[id].deleted)
    .map(id => {
      if (byId[id] && byId[id].element === "component") {
        const component = byId[id];

        const ancestors = getAncestors(component.parentName, byId);
        const value = ancestors ? `${ancestors} ${component.label}` : component.label;

        result.push({
          ...component,
          ancestry: toTitleCase(value)
        });
      }

      if (byId[id] && byId[id].children.length > 0) getComponents(byId[id].children, byId, result);
    });

  return result;
};

export const mergeBuilders = (builder, facilityBuilder) => {
  // Slim checksheet lists omit builder until the full checksheet loads.
  if (!builder?.byId) return {allIds: [], byId: {}};

  const {allIds: taskAllIds, byId: taskById} = builder;
  const {byId: facilityById} = facilityBuilder || {};

  const consolidated = {allIds: taskAllIds, byId: {}};
  Object.values(taskById).map(attributes => {
    const {name, help, children, condition, deleted} = attributes;
    // Consolidate task/facility attributes for groups/components
    if (facilityBuilder && name in facilityById) {
      const taskHelp = help || [];
      const facilityHelp = facilityById[name]?.help || [];
      const facilityDeleted = facilityById[name]?.deleted;
      const facilityCondition = facilityById[name]?.condition;
      const consolidatedHelp = [...facilityHelp, ...taskHelp];
      const consolidatedElement = {
        ...facilityById[name],
        condition: facilityCondition || condition,
        children,
        deleted: facilityDeleted || deleted
      };

      if (consolidatedHelp?.length > 0) consolidatedElement.help = consolidatedHelp;

      consolidated.byId[name] = consolidatedElement;
    } else consolidated.byId[name] = attributes;
  });

  return consolidated;
};

/**
 * @param {object} field
 * @param {object} responses
 * @returns {object}
 */
export const formatFieldResponse = (field, responses) => {
  if (!field) return "";
  const {name, type, on, off} = field;
  let response = responses[name];
  if (response === null || response === undefined) return "";
  if (response === "_CONDITION_UNSATISFIED") return response;
  if (type === "number") response = `${response}`;
  // Use exists() so numeric 0 is not treated as falsy (JS 0 would become "NO PREVIOUS RECORD").
  if (type === "generated") response = exists(response) ? `${response}` : "NO PREVIOUS RECORD";
  if (type === "checkbox") response = response ? "CHECKED" : "UNCHECKED";
  if (type === "confirm") response = response ? response.toUpperCase() : "";
  if (type === "switch") response = response ? on.toUpperCase() : off.toUpperCase();
  if (response?.constructor === Array) response = response.join(", ");

  return response;
};

/**
 * Format responses from form based on builder field types
 * @param {object} responses
 * @param {object} builder
 * @returns {object}
 */
export const formatSubmissions = (responses, builder) => {
  const result = {};

  Object.keys(responses).map(fieldName => {
    const field = builder.byId[fieldName];

    if (
      fieldName.match(/.*_comment$/) ||
      fieldName.match(/.*_qualifier$/) ||
      fieldName.match(/.*_qualifierEnabled$/) ||
      fieldName.match(/.*_rainfall$/) ||
      // `_state` here is a Component Service State value (component enabled/disabled),
      fieldName.match(/.*_state$/)
    )
      result[fieldName] = responses[fieldName];
    else result[fieldName] = formatFieldResponse(field, responses);
  });

  return result;
};

/**
 * Check for unique 'key' within builder element
 * @param {object} builder
 * @param {string} key Element key to compare
 * @param {string} value Value to compare against
 * @param {string} elementType GROUP, COMPONENT or FIELD
 * @returns {boolean} is unique
 */
const isUniqueKey = (builder, key, value, elementType = null) => {
  // remove duplicate spaces
  const sanitized = value.replace(/\s+/g, " ").trim();

  return (
    !Object.keys(builder.byId).filter(current => {
      const targetElement = builder.byId[current];
      if (elementType && elementType === targetElement.element) return false;
      return sanitized.toLowerCase() === targetElement[key];
    }).length > 0
  );
};

/**
 * Check for unique label within builder element
 * @param {object} builder
 * @param {string} value Value to compare against
 * @param {string} elementType GROUP, COMPONENT or FIELD
 */
export const isUniqueLabel = (builder, value, elementType) =>
  isUniqueKey(builder, "label", value, elementType);

/**
 * Return true if field existed on checksheet at time of record
 * @param {string} id
 * @param {object} byId
 * @param {string} dateDue
 * @param {object} responses
 * @returns {boolean}
 */
export const filterNonexistentField = (
  id,
  byId,
  responses,
  allowRemoved = false,
  allowNew = false
) => {
  const item = byId[id];
  const staleShared = !!item.stale && !!byId[item.stale]?.shared;
  if (!item || item.type === "weather" || staleShared) return false;
  if (item.element !== "field") {
    if (!allowRemoved && item.deleted) return false;
    return true;
  }
  // Orphan fields under a soft-deleted component/group should not appear on live sheets
  if (!allowRemoved && hasDeletedAncestor(byId, id)) return false;
  if (!allowRemoved && (item.stale || item.deleted)) return false;
  // if responses is null, we are in an active checksheet, so always show (provided above checks passed)
  // otherwise, hide field if id not in responses
  if (item.stale || item.deleted) return !responses || !!responses[id];
  return allowNew || !responses || id in responses;
};
